import { toastError } from "@/lib/toast-error";
import { useState } from "react";
import { Link, useLocation } from "wouter";
import { useSearchSalesforceOpportunities, useGetSalesforceOpportunity, useCreateDeal } from "@workspace/api-client-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { formatCurrency, formatDate } from "@/lib/format";
import { Search, Building2, Calendar, DollarSign, ArrowLeft, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { useDebounce } from "@/hooks/use-debounce";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

export default function NewDeal() {
  const [, setLocation] = useLocation();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [notes, setNotes] = useState("");

  const { data: searchResults, isLoading: isSearching } = useSearchSalesforceOpportunities(
    { q: debouncedSearch },
    { query: { enabled: debouncedSearch.length >= 2, queryKey: ["/api/salesforce/opportunities/search", { q: debouncedSearch }] } }
  );

  const { data: opportunity, isLoading: isLoadingOpp } = useGetSalesforceOpportunity(
    selectedId || "",
    { query: { enabled: !!selectedId, queryKey: ["/api/salesforce/opportunities", selectedId] } }
  );

  const createDeal = useCreateDeal();

  const handleCreate = () => {
    if (!selectedId) return;

    createDeal.mutate(
      { data: { sfOpportunityId: selectedId, notes: notes || undefined } },
      {
        onSuccess: (deal) => {
          toast.success("Deal registered successfully");
          setLocation(`/deals/${deal.id}`);
        },
        onError: (err: unknown) => {
          toastError(err, "Failed to register deal");
        }
      }
    );
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto animate-in fade-in duration-500">
      <div className="flex items-center gap-4">
        <Link href="/deals">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Register Deal</h1>
          <p className="text-muted-foreground">Select a Salesforce opportunity to begin registration.</p>
        </div>
      </div>

      {!selectedId ? (
        <Card className="border shadow-sm">
          <CardHeader>
            <CardTitle>Search Opportunities</CardTitle>
            <CardDescription>Enter an account or opportunity name (min 2 characters)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search Salesforce..."
                className="pl-9 bg-muted/50"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
              {isSearching && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
              )}
            </div>

            <div className="space-y-2 mt-6">
              {debouncedSearch.length < 2 ? (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  Type at least 2 characters to search.
                </div>
              ) : isSearching ? (
                <div className="space-y-3">
                  {[1, 2, 3].map(i => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : searchResults?.results?.length ? (
                searchResults.results.map(opp => (
                  <button
                    key={opp.id}
                    className="w-full text-left p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors flex items-center justify-between group"
                    onClick={() => setSelectedId(opp.id)}
                  >
                    <div>
                      <div className="font-semibold text-foreground">{opp.name}</div>
                      <div className="text-sm text-muted-foreground flex items-center gap-4 mt-1">
                        <span className="flex items-center gap-1"><Building2 className="h-3 w-3" /> {opp.accountName}</span>
                        <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {formatDate(opp.closeDate)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="font-medium text-foreground">{opp.amount ? formatCurrency(opp.amount) : "—"}</div>
                        <div className="text-xs text-muted-foreground">{opp.stageName}</div>
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </button>
                ))
              ) : (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  No opportunities found matching "{debouncedSearch}".
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6 animate-in slide-in-from-right-8 duration-300">
          <Card className="border shadow-sm overflow-hidden">
            <div className="bg-primary/5 px-6 py-4 border-b flex items-center justify-between">
              <div className="flex items-center gap-2 text-primary font-medium">
                <CheckCircle2 className="h-5 w-5" />
                Opportunity Selected
              </div>
              <Button variant="ghost" size="sm" onClick={() => setSelectedId(null)} className="h-8">
                Change
              </Button>
            </div>
            
            <CardContent className="p-6">
              {isLoadingOpp ? (
                <div className="space-y-4">
                  <Skeleton className="h-8 w-2/3" />
                  <Skeleton className="h-4 w-1/3" />
                  <div className="grid grid-cols-2 gap-4 mt-6">
                    <Skeleton className="h-16 w-full" />
                    <Skeleton className="h-16 w-full" />
                  </div>
                </div>
              ) : opportunity ? (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-foreground">{opportunity.name}</h2>
                    <div className="flex items-center gap-2 text-muted-foreground mt-1">
                      <Building2 className="h-4 w-4" />
                      <span className="font-medium text-foreground">{opportunity.accountName}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-4 bg-muted/30 rounded-lg border">
                    <div>
                      <div className="text-xs text-muted-foreground mb-1">Stage</div>
                      <div className="font-medium">{opportunity.stageName || "—"}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground mb-1">Close Date</div>
                      <div className="font-medium">{formatDate(opportunity.closeDate) || "—"}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground mb-1">MRR</div>
                      <div className="font-medium">{opportunity.mrr ? formatCurrency(opportunity.mrr) : "—"}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground mb-1">TCV</div>
                      <div className="font-medium">{opportunity.tcv ? formatCurrency(opportunity.tcv) : "—"}</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <h3 className="font-semibold mb-3 text-sm uppercase tracking-wider text-muted-foreground">Primary Contact</h3>
                      <div className="space-y-1 text-sm">
                        <div className="font-medium">{opportunity.primaryContactName || "—"}</div>
                        {opportunity.primaryContactEmail && <div>{opportunity.primaryContactEmail}</div>}
                        {opportunity.primaryContactPhone && <div>{opportunity.primaryContactPhone}</div>}
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-3 text-sm uppercase tracking-wider text-muted-foreground">Owner</h3>
                      <div className="space-y-1 text-sm">
                        <div className="font-medium">{opportunity.ownerName || "—"}</div>
                        {opportunity.ownerEmail && <div>{opportunity.ownerEmail}</div>}
                      </div>
                    </div>
                  </div>
                  
                  {opportunity.products && opportunity.products.length > 0 && (
                    <div>
                      <h3 className="font-semibold mb-3 text-sm uppercase tracking-wider text-muted-foreground">Products</h3>
                      <div className="flex flex-wrap gap-2">
                        {opportunity.products.map((p, i) => (
                          <div key={i} className="px-2.5 py-1 bg-secondary text-secondary-foreground rounded-md text-xs font-medium border">
                            {p}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="space-y-2 pt-4 border-t">
                    <Label htmlFor="notes">Registration Notes (Optional)</Label>
                    <Textarea 
                      id="notes" 
                      placeholder="Add any internal notes about this deal..." 
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      className="resize-none"
                    />
                    <p className="text-xs text-muted-foreground">These notes will not be sent to suppliers.</p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-destructive">Failed to load opportunity details.</div>
              )}
            </CardContent>
            <CardFooter className="bg-muted/20 border-t p-6 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setSelectedId(null)}>Cancel</Button>
              <Button onClick={handleCreate} disabled={!opportunity || createDeal.isPending}>
                {createDeal.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Import Deal & Continue
              </Button>
            </CardFooter>
          </Card>
        </div>
      )}
    </div>
  );
}
